import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { QrCode, Loader2, X, CheckCircle2, Package, Truck, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const READER_ID = 'shipment-qr-reader';

/**
 * Modale scanner QR code pour le module colis.
 * - Ouvre la caméra
 * - Décode le QR et extrait le tracking number
 * - Cherche le shipment dans l'agence
 * - Propose actions rapides : voir suivi public / confirmer livraison
 */
export const ShipmentQrScanner = ({ open, onClose, onFound, onDeliver }) => {
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { tn, url }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      setError('');
      setResult(null);
      // Wait for the reader div to be mounted (dialog renders async)
      await new Promise(res => requestAnimationFrame(() => setTimeout(res, 80)));
      if (cancelled) return;
      const el = document.getElementById(READER_ID);
      if (!el) {
        setError('Scanner non initialisé. Fermez et rouvrez.');
        return;
      }
      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => {
            let tn = decodedText.trim();
            const m = tn.match(/\/track\/([A-Z0-9-]+)/i);
            if (m) tn = m[1];
            setResult({ tn: tn.toUpperCase(), url: decodedText });
            scanner.stop().catch(() => {}).finally(() => setRunning(false));
            if (onFound) onFound(tn.toUpperCase(), decodedText);
          },
          () => { /* ignore scan errors */ }
        );
        if (!cancelled) setRunning(true);
      } catch (e) {
        if (!cancelled) setError('Impossible d\'accéder à la caméra. Autorisez l\'accès puis réessayez.');
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try { s.stop().catch(() => {}); } catch { /* noop */ }
        try { s.clear(); } catch { /* noop */ }
      }
    };
  }, [open, onFound]);

  const handleClose = () => {
    const s = scannerRef.current;
    if (s) {
      try { s.stop().catch(() => {}); } catch { /* noop */ }
    }
    setRunning(false);
    setResult(null);
    setError('');
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="shipment-qr-scanner">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2B3A2C]">
            <QrCode className="w-5 h-5 text-[#B85C38]" />
            Scanner un bordereau colis
          </DialogTitle>
          <DialogDescription>
            Pointez la caméra vers le QR code du bordereau. Le numéro de suivi sera reconnu automatiquement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Reader container */}
          <div className="rounded-xl overflow-hidden border-2 border-[#B85C38]/30 bg-black/90 relative"
               style={{ minHeight: '280px' }}>
            <div id={READER_ID} style={{ width: '100%' }} />
            {!running && !result && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Activation de la caméra…
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="scanner-error">
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 space-y-3" data-testid="scanner-result">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">QR détecté</p>
                  <p className="text-xl font-mono font-bold text-[#2B3A2C] mt-1" data-testid="scanned-tn">{result.tn}</p>
                  {result.url !== result.tn && (
                    <p className="text-[10px] text-emerald-700 mt-1 truncate">{result.url}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.url.startsWith('http') && (
                  <Button size="sm" variant="outline" onClick={() => window.open(result.url, '_blank')}
                          className="border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                          data-testid="open-tracking-btn">
                    <ExternalLink className="w-4 h-4 mr-1" /> Voir suivi public
                  </Button>
                )}
                {onDeliver && (
                  <Button size="sm" onClick={() => onDeliver(result.tn)}
                          className="bg-[#B85C38] hover:bg-[#8F4426] text-white"
                          data-testid="scan-deliver-btn">
                    <Truck className="w-4 h-4 mr-1" /> Confirmer la livraison
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => {
                  setResult(null);
                  handleClose();
                  setTimeout(() => onClose && onClose('rescan'), 100);
                }} className="text-gray-600">
                  <Package className="w-4 h-4 mr-1" /> Voir dans la liste
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose} data-testid="close-scanner-btn">
              <X className="w-4 h-4 mr-1" /> Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShipmentQrScanner;
